import { getExogenousDisplayFormat } from '@components/model-structure/exogenous-lines/exogenous.spread.config';
import * as GC from '@grapecity/spread-sheets';
import { clone } from '@helpers/arrays';
import { convertScientificNumberToText } from '@helpers/convert-scientific-number-to-text';
import { DiscStartCellType } from '@helpers/spread/cell-types/disc-start-cell-type';
import * as SheetFunctions from '@helpers/spread/sheet-functions';
import { KRITable } from '@models/kri-table';
import { ActiveHighlights, ActiveHighlightsSelection, MyDateTime } from '@models/results/results-class-library';
import { ResultsetItem } from '@models/resultset-item';
import { HIGHLIGHTS_TAB } from '@services/results/result-tabs.service';
import { ResultsStoreService } from '@services/results/results-store.service';
import { getFont } from '@shared/spreadsheet/spreadsheet-config';
import { isEqual } from 'lodash-es';
import { addChartToSheet } from './chart-builder';
import { doesItFailsAtRiskLimitTest } from './exogenous-builder';
import {
  CHART_HEIGHT,
  COL_WIDTH,
  FAIL_COLOR,
  FAIL_TEXT,
  INVALID_PERIOD_ZERO_DATE_MESSAGE,
  PASS_COLOR,
  PASS_TEXT,
  ROW_COUNT,
  ROW_HEIGHT,
  ROW_HEIGHT_TITLE,
  STRESS_COLOR,
  UNGROUPED_NAME,
  WARNING_COLOR,
  WARNING_TEXT,
} from './results-spread-config';

let currentRow: number;
let chartCorrectorFactor: number;
let asOfDate: any;
let spread: GC.Spread.Sheets.Workbook;
let resultsComponent;
let resultsList;
let resultsStore: ResultsStoreService;

// Highlights View
export function buildAndAddHighlightsToSpread(workbook: GC.Spread.Sheets.Workbook, results: ResultsetItem[], component) {
  resultsList = results;
  spread = workbook;
  asOfDate = component.asOfDate;
  resultsComponent = component;
  resultsStore = component.resultsStore;

  currentRow = 0;
  resetHighlightsSelections();
  const sheet: GC.Spread.Sheets.Worksheet = new GC.Spread.Sheets.Worksheet(HIGHLIGHTS_TAB);

  // Events
  // ===============================
  sheet.bind(GC.Spread.Sheets.Events.TopRowChanged, function (sender, args) {
    ActiveHighlights.row = args.newTopRow;
    ActiveHighlights.last_change = MyDateTime.getCurrentDateTime();
    // console.log('activeSummaryRow', ActiveSummary.row, ActiveSummary.last_change);
  });

  sheet.bind(GC.Spread.Sheets.Events.CellClick, function (e, info) {
    ActiveHighlightsSelection.row = info.row;
    ActiveHighlightsSelection.col = info.col;
    ActiveHighlightsSelection.last_change = MyDateTime.getCurrentDateTime();
    // console.log('activeSummarySelection', ActiveSummarySelection);
  });

  sheet.bind(GC.Spread.Sheets.Events.CellDoubleClick, function (e, info) {
    ActiveHighlightsSelection.row = info.row;
    ActiveHighlightsSelection.col = info.col;
    ActiveHighlightsSelection.last_change = MyDateTime.getCurrentDateTime();
    // console.log('activeSummarySelection', ActiveSummarySelection);
  });
  // const sheetIndex = 1;

  try {
    // Part of Fix 3343
    resetChartCorrectorFactor();

    if (resultsList.length > 0) {
      const baselines = resultsList.filter(s => !s.Is_Stress && !s.Hidden && s.Results);

      if (baselines && baselines.length > 0) {
        sheet.suspendPaint();

        const periodDatesLength: number = baselines[0].Results.PeriodDates.length > 0 ? baselines[0].Results.PeriodDates.length : 0;

        const Survival_Horizon = baselines[0].Results.Survival_Horizon;
        const numColumns = calculateSheetColumns(sheet, { baselines, numPeriods: periodDatesLength });
        sheet.setColumnCount(numColumns);
        // Enough rows to fit the overall use of the spreadsheet
        sheet.setRowCount(ROW_COUNT);

        const proportionalWidth = Math.round(document.querySelector('body').clientWidth / (sheet.getColumnCount() || numColumns)) - 4;
        const minWidth = 74;

        sheet.options.isProtected = true;
        sheet.defaults.rowHeight = ROW_HEIGHT;
        sheet.defaults.colWidth = proportionalWidth > minWidth ? proportionalWidth : minWidth;
        // Task 3424 - Part 1: Making the grid lines of the spreadsheet transparent
        sheet.options.gridline.showHorizontalGridline = false;
        sheet.options.gridline.showVerticalGridline = false;

        currentRow = 1;

        // Task 3424 - Part 5: Set 'A', 'B', 'C' columns more narrow to make the report left centered
        [20, 20, 50].forEach((colWidth, colIndex) => sheet.setColumnWidth(colIndex, colWidth));

        sheet.setColumnWidth(3, 250, GC.Spread.Sheets.SheetArea.viewport);

        const currentCol = 2; // Starting column

        // Sheet Title
        let cell = sheet.getCell(1, currentCol);
        cell.value('Liquidity Stress - High Level Summary').wordWrap(false);
        cell.font(getFont('xx-lg', true));
        chartCorrectorFactor++;
        sheet.addSpan(currentRow, currentCol, 1, 4, GC.Spread.Sheets.SheetArea.viewport);
        SheetFunctions.centerAndWrapCell(spread, sheet, currentRow, currentCol);
        sheet.setRowHeight(currentRow, ROW_HEIGHT_TITLE, GC.Spread.Sheets.SheetArea.viewport);
        cell.hAlign(GC.Spread.Sheets.HorizontalAlign.left);
        cell.vAlign(GC.Spread.Sheets.VerticalAlign.center);

        // As Of date
        currentRow++;
        cell = sheet.getCell(currentRow, currentCol);
        cell.value('As of:');
        cell.font(getFont('md'));
        SheetFunctions.centerAndWrapCell(spread, sheet, currentRow, currentCol);
        cell.hAlign(GC.Spread.Sheets.HorizontalAlign.left);
        cell.vAlign(GC.Spread.Sheets.VerticalAlign.center);

        // Part of fix 3532 and 3604: Make sure the "as of" date is the 0 period date in the Global_Params table
        cell = sheet.getCell(currentRow, currentCol + 1);
        const date = asOfDate;
        const periodDateFailed = !asOfDate;
        if (periodDateFailed) {
          console.warn('Invalid period 0 date being passed as parameter to buildCoverPageSheet()', {
            asOfDate,
          });
          cell.value(INVALID_PERIOD_ZERO_DATE_MESSAGE).foreColor('red').backColor(FAIL_COLOR);
        } else {
          SheetFunctions.setOutputReportDate(spread, sheet, cell, date as Date, true, null);
        }
        cell.font(getFont('md', periodDateFailed));
        SheetFunctions.centerAndWrapCell(spread, sheet, 2, currentCol + 1);
        cell.hAlign(GC.Spread.Sheets.HorizontalAlign.left);
        cell.vAlign(GC.Spread.Sheets.VerticalAlign.center);

        currentRow++;
        cell = sheet.getCell(currentRow, currentCol);
        cell.value('Scale:');
        cell.font(getFont('md'));
        SheetFunctions.centerAndWrapCell(spread, sheet, currentRow, currentCol);
        cell.hAlign(GC.Spread.Sheets.HorizontalAlign.left);

        cell = sheet.getCell(currentRow, currentCol + 1);
        cell.value('Millions');
        cell.font(getFont('md'));
        SheetFunctions.centerAndWrapCell(spread, sheet, currentRow, currentCol + 1);
        cell.hAlign(GC.Spread.Sheets.HorizontalAlign.left);

        // spread.addSheet(sheetIndex, sheet);
        spread.addSheet(0, sheet);

        currentRow = 4;
        sheet.floatingObjects.clear();
        sheet.charts.clear();

        // Display each simulation set
        baselines.forEach((baseline: ResultsetItem, index: number) => {
          if (index !== 0) {
            // Fix 3532 Part 2 updated
            // -- Margin-top and Double-border break for the simulation set (except for the first table)
            currentRow += 2;
            const pageBreakRange = sheet.getRange(currentRow, 0, 1, sheet.getColumnCount(GC.Spread.Sheets.SheetArea.viewport));
            pageBreakRange.borderTop(new GC.Spread.Sheets.LineBorder('Black', GC.Spread.Sheets.LineStyle.double));
            currentRow += 4;
          }
          displayPeriodNumberHeaders(sheet, currentCol, periodDatesLength);
          // Add dotted border to period columns
          displayPeriodColumnSeparator(sheet, currentRow - 1, currentCol + 2, baseline, periodDatesLength);

          // Modifying emptyKRISourceData could reintroduce the defect 3987
          const emptyKRISourceData = {
            title: undefined,
            rowHeaders: undefined,
            colHeaders: undefined,
            values: undefined,
          };
          let KRISourceData = clone(emptyKRISourceData);

          let range = '';
          currentRow = DisplayHighlightMnemonicData(sheet, currentRow, currentCol, baseline, periodDatesLength, Survival_Horizon);
          range = `${range + (currentRow - 1)},`;

          // Stresses
          if (baseline.StressList) {
            baseline.StressList.forEach(stress => {
              if (!stress.Hidden) {
                currentRow = DisplayHighlightMnemonicData(sheet, currentRow, currentCol, stress, periodDatesLength, Survival_Horizon);
                range = `${range + (currentRow - 1)},`;
              }
            });
          }

          range = range.replace(/,\s*$/, '');

          // Simulation Set's CHART
          // ----------------------
          currentRow++; // chart margin-top
          currentRow = addChartToSheet(
            sheet,
            range,
            baseline,
            currentRow,
            periodDatesLength - 1,
            currentCol,
            chartCorrectorFactor,
            resultsComponent.settingsService
          );
          currentRow++; // chart margin-bottom

          // KRI Table
          // -- Build Highlights-view KRIs table with Display_In_Summary Filtering and displaying last period only

          // Add KRI Data
          KRISourceData = addKRISourceDataFrom(baseline, KRISourceData);
          if (!isEqual(KRISourceData, emptyKRISourceData)) {
            currentRow = addKRIsTable(sheet, currentRow, currentCol, KRISourceData);
          }

          // Margin-bottom
          currentRow++;
        });

        SheetFunctions.autoFitCell(spread, sheet, -1, -1);

        // Delete extra unused rows
        currentRow += 5;
        sheet.deleteRows(currentRow - 1, sheet.getRowCount() - currentRow);

        sheet.frozenColumnCount(4);

        sheet.resumePaint();
      }
    }
  } catch (err) {
    console.warn('Error while trying to load the chart library', err);
  } finally {
    return sheet;
  }
}

function resetHighlightsSelections() {
  ActiveHighlights.row = 0;
  ActiveHighlights.col = 0;
  ActiveHighlights.last_change = null;

  ActiveHighlightsSelection.row = 0;
  ActiveHighlightsSelection.col = 0;
  ActiveHighlightsSelection.last_change = null;
}

function resetChartCorrectorFactor() {
  chartCorrectorFactor = 0;
}

function calculateSheetColumns(sheet: GC.Spread.Sheets.Worksheet, params: object): number {
  if (sheet.name() === HIGHLIGHTS_TAB) {
    const prePeriodColumnNum = 4;

    // Task 3626: Adapt the columns to the number of stresses displayed in the KRIs
    const getColumnsToFit = () => {
      const colSpan = 2;

      // KRI Stresses should fit
      const numVisibleStresses = params['baselines'].reduce((acc: number, baseline: ResultsetItem) => {
        const stNum = baseline.hasOwnProperty('StressList') ? baseline.StressList.filter(s => !s.Hidden).length : 0;
        return stNum > acc ? stNum : acc;
      }, 0);

      // Period Numbers should fit
      const extraColsKRIsBased = 2 + (numVisibleStresses + 1) * colSpan || 0;
      const extraColsPeriodsBased = params['numPeriods'] - 1 || 0;

      const finalExtraCols = Math.max(extraColsKRIsBased, extraColsPeriodsBased);
      return finalExtraCols > 0 ? finalExtraCols + 1 : 0;
    };

    // num of columns prior period numbers + period numbers columns + extra columns to fit KRI' stresses
    const numColumns = prePeriodColumnNum + getColumnsToFit();
    return numColumns > 0 ? numColumns : 0;
  }
}

function displayPeriodNumberHeaders(sheet: GC.Spread.Sheets.Worksheet, col: number, numPeriods: number) {
  chartCorrectorFactor++;

  let cell;
  for (let i = 1; i < numPeriods; i++) {
    const currRow = currentRow - 1;
    const currentPeriodColumn = col + i + 1;
    cell = sheet.getCell(currRow, currentPeriodColumn);
    cell.value(i);
    cell.font(getFont('lg', true));
    SheetFunctions.centerAndWrapCell(spread, sheet, currRow, currentPeriodColumn);

    // Task 3428
    sheet.setRowHeight(currRow, ROW_HEIGHT_TITLE, GC.Spread.Sheets.SheetArea.viewport);
    cell.textDecoration(GC.Spread.Sheets.TextDecorationType.underline);
    SheetFunctions.centerAndWrapCell(spread, sheet, currRow, currentPeriodColumn);

    // Task 3424 - Part 4: Setting the same column width for the period columns
    sheet.setColumnWidth(currentPeriodColumn, COL_WIDTH, GC.Spread.Sheets.SheetArea.viewport);
  }
}

function displayPeriodColumnSeparator(
  sheet: GC.Spread.Sheets.Worksheet,
  row: number,
  col: number,
  baseline: ResultsetItem,
  numPeriods: number
) {
  const isValidBaseline = baseline && !baseline.Is_Stress && !baseline.Hidden;
  if (isValidBaseline) {
    // Helper function
    const applyBorder = (_row: number, _col: number, isCut: boolean = false) => {
      const _rowsAffected = rowsAffected - (isCut ? graphRows - 2 : 0);
      sheet
        .getRange(_row, _col, _rowsAffected, 1)
        .setBorder(new GC.Spread.Sheets.LineBorder('#3f3f3f', GC.Spread.Sheets.LineStyle.dotted), {
          all: false,
          left: true,
          right: true,
        });
    };

    // Calculate the coordinates for each row to apply the border to
    const rowsByScen = 4;
    let scenNum = 1;
    if (isValidBaseline && baseline.StressList && numPeriods) {
      scenNum += baseline.StressList.filter(stress => !stress.Hidden).length;
    }
    const graphRows = Math.round(CHART_HEIGHT / ROW_HEIGHT);
    // Border is used by baseline + stresses + graph
    // -- It should not before the legend row and It should not cover the graph for the first and last period columns
    const rowsAffected = Math.round(scenNum * rowsByScen + graphRows - 1);

    // All period columns should overlap the chart, except the first one and the last one
    // that should end at the row of the survival horizon
    const middleRangeColumnIndexes = [];
    for (let i = 1; i < numPeriods - 2; i++) {
      middleRangeColumnIndexes.push(col + i);
    }
    const cuttedBorderColumnIndexes = [col, middleRangeColumnIndexes[middleRangeColumnIndexes.length - 1] + 1];

    // Apply the proper border for each column
    cuttedBorderColumnIndexes.forEach((_col: number) => applyBorder(row, _col, true));
    middleRangeColumnIndexes.forEach((_col: number) => applyBorder(row, _col, false));
  }
}

function DisplayHighlightMnemonicData(
  sheet: GC.Spread.Sheets.Worksheet,
  row: number,
  col: number,
  resultsetItem: ResultsetItem,
  numPeriods: number,
  Survival_Horizon: number
): number {
  col = 4;
  let cell = sheet.getCell(row, 2);
  const color = resultsetItem.Is_Stress ? STRESS_COLOR : '';

  cell.text(resultsetItem.Mnemonic);
  cell.font(getFont('md', true));
  // Task 3429: Display a color circle using scenario-based color
  if (resultsetItem) {
    addScenarioColorDisc(cell, resultsetItem, null);
  }

  sheet.addSpan(row, 2, 1, 2, GC.Spread.Sheets.SheetArea.viewport);
  SheetFunctions.centerAndWrapCell(spread, sheet, row, 2);
  cell.hAlign(GC.Spread.Sheets.HorizontalAlign.left);

  // Scenario title row long border
  for (let i = 0; i < numPeriods + 1; i++) {
    sheet
      .getCell(row, 2 + i, GC.Spread.Sheets.SheetArea.viewport)
      .borderBottom(new GC.Spread.Sheets.LineBorder('Black', GC.Spread.Sheets.LineStyle.thin));
  }

  row++;
  const standardFontSize = 'md';

  cell = sheet.getCell(row, 3);
  cell.value('Cumulative Sources/Uses');
  cell.font(getFont(standardFontSize, false, true));
  SheetFunctions.centerAndWrapCell(spread, sheet, row, 3);
  cell.hAlign(GC.Spread.Sheets.HorizontalAlign.left);

  const cumulativeSourceUse = resultsetItem.Results.HighLevelSummary.Cumulative.Category.Properties.SourceUse || '';
  const balance = resultsetItem.Results.HighLevelSummary.RemainingCapacity;

  for (const key of Object.keys(cumulativeSourceUse.Results)) {
    const val = Number(cumulativeSourceUse.Results[key].Value);
    const rounding = true;
    cell = sheet.getCell(row, col);
    SheetFunctions.setOutputReportNumberFormatting(spread, sheet, row, col, val, null, rounding);
    resultsStore.flagDisplayZeroAsForConversionOnExport(sheet, cell);
    // Font overwrite
    cell.font(getFont(standardFontSize, false, false));
    col++;
  }

  row++;
  cell = sheet.getCell(row, 3);
  cell.value('Available Remaining Capacity').font(getFont(standardFontSize, false, true));
  SheetFunctions.centerAndWrapCell(spread, sheet, row, 3);
  cell.hAlign(GC.Spread.Sheets.HorizontalAlign.left);

  cell = sheet.getCell(row + 1, 3);
  cell.value(`Survival Horizon: ${resultsetItem.Results.HighLevelSummary.PassCount}`);
  cell.font(getFont(standardFontSize, false, true));
  SheetFunctions.centerAndWrapCell(spread, sheet, row + 1, 3);
  cell.hAlign(GC.Spread.Sheets.HorizontalAlign.left);

  col = 4;
  for (const key of Object.keys(balance.Results)) {
    let _color = '';
    let text = '';
    const survival: number = Survival_Horizon / 1_000_000;
    if (balance.Results[key].Pass) {
      if (balance.Results[key].RemainingBalance > survival) {
        text = PASS_TEXT;
        _color = PASS_COLOR;
      } else {
        text = WARNING_TEXT;
        _color = WARNING_COLOR;
      }
    } else {
      text = FAIL_TEXT;
      _color = FAIL_COLOR;
    }

    // Available Remaining Capacity Value
    const val = balance.Results[key].RemainingBalance;
    const rounding = true;
    cell = sheet.getCell(row, col);
    SheetFunctions.setOutputReportNumberFormatting(spread, sheet, row, col, val, null, rounding);
    resultsStore.flagDisplayZeroAsForConversionOnExport(sheet, cell);

    // font overwrite
    cell.font(getFont(standardFontSize));

    // Survival Horizon test PASS / FAIL text
    cell = sheet.getCell(row + 1, col);
    cell.value(text).backColor(_color).font(getFont(standardFontSize));
    SheetFunctions.centerAndWrapCell(spread, sheet, row + 1, col);
    col++;
  }
  row += 2;
  return row;
}

function addKRISourceDataFrom(resultset: ResultsetItem, _KRITableData: KRITable): KRITable {
  if (!resultset.Hidden) {
    Object.keys(resultset.Results.Exogenous).forEach((exogKey: string) => {
      const _exog = resultset.Results.Exogenous[exogKey];
      const groupId = _exog.GroupID;
      if (!_KRITableData[groupId]) {
        _KRITableData[groupId] = {};
      }
      if (!_KRITableData[groupId][resultset.Mnemonic]) {
        _KRITableData[groupId][resultset.Mnemonic] = [];
      }
      if (_KRITableData[groupId][resultset.Mnemonic].length === 0) {
        _KRITableData[groupId][resultset.Mnemonic] = Object.keys(resultset.Results.Exogenous)
          .filter(
            exoName =>
              // Exogs Filtered by GroupID (case insensitive)
              resultset.Results.Exogenous[exoName].GroupID.toLowerCase() === groupId.toLowerCase() &&
              // DIS Filtering (Display In Summary Filter) on Highlights view
              resultset.Results.Exogenous[exoName].Display_In_Summary
          )
          .map(exoName => resultset.Results.Exogenous[exoName]);
      }
    });

    // Add KRI Data from his stresses
    if (!resultset.Is_Stress && resultset.StressList) {
      resultset.StressList.forEach(stress => (_KRITableData = addKRISourceDataFrom(stress, _KRITableData)));
    }
  }

  return _KRITableData;
}

function addKRIsTable(sheet: GC.Spread.Sheets.Worksheet, row: number, col: number, sourceData: any) {
  // Iterate through each GroupId to create a KRI table
  const groupIdList = sourceData && Object.keys(sourceData);

  // Helper
  const paintGroupdIdTable = (KRIGroupID: string, tableIndex: number) => {
    let monthBoxPeriodNum = 0;
    let lastPeriodIndex = 0;
    const tableValues: any[][] = [];
    const tableTests: any[][] = [];
    const displayFormatters: object = {};
    const resultsets = sourceData[KRIGroupID];
    const resultsetsMnemonics = resultsets && Object.keys(resultsets); // .sort()

    if (resultsetsMnemonics && resultsetsMnemonics.length > 0) {
      const exogsMnemonics = clone(resultsets[resultsetsMnemonics[0]]).map(exog => exog.Mnemonic);
      const exogsNames = clone(resultsets[resultsetsMnemonics[0]]).map(exog => exog.Name);

      exogsMnemonics.forEach((exogMn, rowHeaderIndex) => {
        const scenarioBasedValues = [];
        const scenarioBasedTests = [];
        const baselineExogs = resultsets[resultsetsMnemonics[0]];
        const baselineExog = baselineExogs.find(exog => exog.Mnemonic === exogMn);
        const riskLimit = baselineExog.Risk_Limit;
        const failOperator = baselineExog.Fail_Operator;
        const currentPeriodValue = baselineExog.Results['0'].Value;

        Object.keys(resultsets).forEach(resultsetMn => {
          const currentResultsetExog = resultsets[resultsetMn].find(exog => exog.Mnemonic === exogMn);
          if (!currentResultsetExog) return;
          const highestPeriod = Object.keys(currentResultsetExog.Results).reduce((a, b) => {
            return +a > +b ? a : +b;
          }, 0);

          let worstCasePeriodValue = undefined;
          Object.keys(currentResultsetExog.Results).forEach(exogenousResultIndex => {
            const currentPeriodValue = currentResultsetExog.Results[exogenousResultIndex].Value;
            if (worstCasePeriodValue === undefined) {
              worstCasePeriodValue = currentPeriodValue;
            } else if (failOperator === '<' && worstCasePeriodValue > currentPeriodValue) {
              worstCasePeriodValue = currentPeriodValue;
            } else if (failOperator === '>' && worstCasePeriodValue < currentPeriodValue) {
              worstCasePeriodValue = currentPeriodValue;
            } else if (!failOperator) {
              worstCasePeriodValue = currentPeriodValue;
            }
          });

          scenarioBasedValues.push(worstCasePeriodValue);
          scenarioBasedTests.push(doesItFailsAtRiskLimitTest(worstCasePeriodValue, failOperator, riskLimit));
          tableValues[rowHeaderIndex] = [
            riskLimit,
            currentPeriodValue,
            // Baseline, Downturn, Worst case and all scenario-based values...
            ...scenarioBasedValues,
          ];
          tableTests[rowHeaderIndex] = scenarioBasedTests;

          // MONTH BOX PERIOD NUMBER:
          // -- Set the periodNum for the Month Box
          if (!lastPeriodIndex || !monthBoxPeriodNum) {
            lastPeriodIndex = Object.keys(currentResultsetExog.Results).length - 1; // This line may be deprecated
            monthBoxPeriodNum = highestPeriod;
          }

          // Add formatter for this exog.Line
          if (!displayFormatters[currentResultsetExog.Mnemonic]) {
            displayFormatters[currentResultsetExog.Mnemonic] = getExogenousDisplayFormat(currentResultsetExog.Display_format);
          }
        });
      });

      // Build the table
      const table: KRITable = {
        title: KRIGroupID || UNGROUPED_NAME,
        rowHeaders: exogsNames,
        colHeaders: <string[]>[
          'Risk Limit',
          'Current',
          // Scenario based headers: Baseline, Downturn Case, Worst case, ...
          ...resultsetsMnemonics,
        ],
        values: tableValues,
        tests: tableTests,
        displayFormatters,
      };

      if (table.rowHeaders && table.rowHeaders.length > 0) {
        const showMonthBox = table.title !== UNGROUPED_NAME && 0 === tableIndex;
        // Paint the table once all the table is built
        row = paintKRITable(sheet, row, col, table, showMonthBox, monthBoxPeriodNum);
      }
    }
  };

  // Grouped first & Ungrouped at the bottom
  // -- Grouped
  groupIdList.filter(group => group !== UNGROUPED_NAME).forEach(paintGroupdIdTable);
  // -- Ungrouped
  paintGroupdIdTable(UNGROUPED_NAME, groupIdList.length - 1);

  return row;
}

function paintKRITable(
  sheet: GC.Spread.Sheets.Worksheet,
  row: number,
  col: number,
  table: KRITable,
  showMonthBox = false,
  periodNum: number = 0
) {
  if (showMonthBox) {
    // It's first table
    row++;
  }

  // Paint the table elements
  const titleSpan = 2;
  setKRITableTitle(spread, table.title, titleSpan, row, col);
  chartCorrectorFactor++;
  setKRIColumnHeaders.bind(this)(
    table.colHeaders,
    getOrderedResultsList(table.colHeaders.slice(2, table.colHeaders.length), resultsList),
    row,
    col + titleSpan
  );
  if (showMonthBox) {
    // Display it over the first GroupId only
    setKRIMonthBox(spread, periodNum, row - 1, col + titleSpan + 2);
  } else {
    // Fix 3336 to solve overlapping between tables
    row++;
  }
  // row++;

  setRowHeaders(spread, table.rowHeaders, row, col + 1);
  setKRITableValues.bind(this)(table, row, col + titleSpan);
  // End of painting table elements and related visual adjustements

  // ----------------
  // Helper functions
  // ----------------
  function getOrderedResultsList(scenColHeaders, resultsList) {
    const orderedResultsList: ResultsetItem[] = [];
    scenColHeaders.forEach(scenColHeader => {
      const resultFound = resultsList.filter(s => !s.Hidden).find(evaluatedResult => evaluatedResult.Mnemonic === scenColHeader);
      if (resultFound) {
        orderedResultsList.push(resultFound);
      }
    });
    return orderedResultsList;
  }

  function setKRITableTitle(spread, title: string, colSpan: number, _row: number, _col: number) {
    const cell = sheet.getCell(_row, _col, GC.Spread.Sheets.SheetArea.viewport);

    // KRI Table Title
    cell.value(title);
    cell.font(getFont('lg', true, true));
    sheet.addSpan(_row, _col, 1, colSpan, GC.Spread.Sheets.SheetArea.viewport);
    SheetFunctions.centerAndWrapCell(spread, sheet, _row, _col);
    sheet.setRowHeight(_row, ROW_HEIGHT_TITLE, GC.Spread.Sheets.SheetArea.viewport);
    cell.hAlign(GC.Spread.Sheets.HorizontalAlign.left);
  }

  function setRowHeaders(spread, _rowHeaders: string[] = [], _row: number, _col: number) {
    _rowHeaders.forEach(rowHeader => paintTableRowHeader(rowHeader));

    // encapsulated helper function
    function paintTableRowHeader(rowHeader: string) {
      const colSpan = 1;
      const cell = sheet.getCell(_row, _col, GC.Spread.Sheets.SheetArea.viewport);

      try {
        sheet.addSpan(_row, _col, 1, colSpan);
        SheetFunctions.centerAndWrapCell(spread, sheet, _row, _col);
      } catch (err) {
        // If this error is caught is because we are trying to merge an already merged row!
        // which is a KRI table title so we need to skep it
      }

      cell.value(rowHeader);
      cell.font(getFont('md'));
      SheetFunctions.centerAndWrapCell(spread, sheet, _row, _col);
      cell.hAlign(GC.Spread.Sheets.HorizontalAlign.left);

      _row++;
    }
  }

  function setKRIColumnHeaders(columnTitles: string[] = [], _resultsList: any[] | ResultsetItem[], _row: number, _col: number) {
    const columnsBeforeScenarioHeaders = 2;
    columnTitles.forEach((colTitle, colIndex) => {
      // Exclude previous non-scenario-based-values (Risk Limit & Current)

      // Paint column header
      // -------------------
      const cell = sheet.getCell(_row, _col, GC.Spread.Sheets.SheetArea.viewport);
      const colSpan = colIndex < 2 ? 1 : 2; // Do not expand the first two columns of the table row headers
      const font = getFont('md', true);

      cell.text(colTitle);
      cell.font(font);

      // Task 3424 - Part 3A: Making the columns for the KRI Table values occupy two columns (Headers)
      try {
        sheet.addSpan(_row, _col, 1, colSpan);
      } catch (err) {
        // If this error is caught is because we are trying to merge an already merged row!
      }

      // SheetFunctions.autoFitCell(spread, sheet, _row, _col);
      cell.vAlign(GC.Spread.Sheets.VerticalAlign.center);
      cell.hAlign(GC.Spread.Sheets.HorizontalAlign.center);

      if (colIndex >= 2) {
        // Task 3429: Color disc & Ellipsis
        const resultsList: ResultsetItem[] =
          colIndex >= columnsBeforeScenarioHeaders ? _resultsList[colIndex - columnsBeforeScenarioHeaders] : [];
        if (resultsList) {
          const ellipsisMaxWidth =
            colSpan * (COL_WIDTH - 20) -
            // minus the space that the color disc has displaced the text to the right
            (colIndex >= 2 ? 20 : 0);
          addScenarioColorDisc.bind(this)(cell, resultsList, ellipsisMaxWidth);
          cell.wordWrap(false);

          const charAvgWidth =
            parseInt((font.replace('bold', '').replace('italic', '').replace(' ', '').trim().split('pt') as any)[0], 10) *
            // Conversion to px
            0.75;

          const textIsTooLarge = colTitle.length * charAvgWidth >= ellipsisMaxWidth + 20;
          if (textIsTooLarge) {
            // If the cell has ellipsis in the original sheet, we align it to the left because it will be truncated
            cell.hAlign(GC.Spread.Sheets.HorizontalAlign.left);
          }
        }
      }

      _col += colSpan; // Increase it and add the cell's span too
    });
  }

  function setKRITableValues(_table: KRITable, _row: number, _col: number) {
    // Set the row values:
    const exogMnemonics = Object.keys(table.displayFormatters);
    Object.keys(_table.values).forEach((rowNum: string, index: number) => {
      const currentRowScenBasedValues = _table.values[index];
      // Fixes related 3344, 3187, 3609, 3665, ...
      const currentExogDisplayFormatter = _table.displayFormatters[exogMnemonics[index]];

      // At month-12 values
      currentRowScenBasedValues.forEach((cellValue, ind) => {
        // At month-12-like values
        const isAtMonthXValue = ind > 1; // >= 2
        // Do not expand the first two columns of the table row headers
        const colSpan = isAtMonthXValue ? 2 : 1;
        const _irow = _row;
        const _icol = _col + ind * colSpan - (isAtMonthXValue ? 2 : 0);
        const cell = sheet.getCell(_irow, _icol, GC.Spread.Sheets.SheetArea.viewport);
        const hAlignment = GC.Spread.Sheets.HorizontalAlign.right;

        if (isAtMonthXValue) {
          // Accounting formatting (part of Fix 3665)
          SheetFunctions.setOutputReportNumberFormatting(spread, sheet, _irow, _icol, cellValue, currentExogDisplayFormatter, false);
          resultsStore.flagDisplayZeroAsForConversionOnExport(sheet, cell);

          // Test this cell against his risk limit
          // -- exclude risk limit and current period value. They are the first two columns
          const shouldFail = _table.tests[index][ind - 2];
          if (shouldFail) {
            cell.backColor(FAIL_COLOR);
          }

          // Task 3424 - Part 3B: Making the columns for the KRI Table values occupy two columns (Table values)
          try {
            sheet.addSpan(_irow, _icol, 1, colSpan);
            SheetFunctions.centerAndWrapCell(spread, sheet, _irow, _icol);
            cell.hAlign(hAlignment);
          } catch (err) {
            // If this error is caught is because we are trying to merge an already merged cell!
          }
        } else {
          // Risk Limit & Current should have ellipsis
          const valueFullText = convertScientificNumberToText(cellValue);
          cell.text(valueFullText).formatter(currentExogDisplayFormatter);
          const maxWidthEllipsis = colSpan * sheet.getColumnWidth(cell.col) - 10;
          resultsStore.flagEllipsisForConversionOnExport(sheet, cell, cellValue as number, maxWidthEllipsis);
          SheetFunctions.applyEllipsisToCell(cell, maxWidthEllipsis);
          SheetFunctions.centerAndWrapCell(spread, sheet, _irow, _icol);
          cell.hAlign(hAlignment);
        }

        cell
          // Font overwrite
          .font(getFont('md'))
          // Ensure right-alignment
          .hAlign(hAlignment);
      });
      _row++;
    });
    row = _row++;
  }

  function setKRIMonthBox(spread: GC.Spread.Sheets.Workbook, _periodNum: number, _row: number, _col: number) {
    const cell = sheet.getCell(_row, _col, GC.Spread.Sheets.SheetArea.viewport);
    // const cellValue = `at Month-${_periodNum}`;
    const cellValue = `Horizon Min (Max)`;

    cell.value(cellValue);
    cell.font(getFont('md'));

    // Task 3424 - Part 3C: Making the columns for the KRI Table values occupy two columns (Monthbox width)
    const cellSpan = (table.colHeaders.length - 2) * 2;
    sheet.addSpan(_row, _col, 1, cellSpan, GC.Spread.Sheets.SheetArea.viewport);

    sheet
      .getRange(_row, _col, 1, cellSpan, GC.Spread.Sheets.SheetArea.viewport)
      .setBorder(new GC.Spread.Sheets.LineBorder('#3f3f3f', GC.Spread.Sheets.LineStyle.thin), { all: true });

    SheetFunctions.centerAndWrapCell(spread, sheet, _row, _col);
    cell.hAlign(GC.Spread.Sheets.HorizontalAlign.center);

    row++;
  }

  return row;
}

function addScenarioColorDisc(cell: GC.Spread.Sheets.CellRange, resultsetItem: ResultsetItem, ellipsisMaxWidth: number): void {
  const color = resultsComponent.settingsService.getScenarioColor(resultsetItem) || '#cccccc';
  // console.log(`Setting KRI's color disc of ${sum.Mnemonic} to ${color}`);
  const displayBorder = false;
  // Convert to Disc Cell Type
  cell.cellType(DiscStartCellType(color, displayBorder, ellipsisMaxWidth));
}
